import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Asset } from '../../types/asset.types';
import { Link, useNavigate } from 'react-router-dom';
import { formatCompositeHfn } from '../../utils/compositeAssetFormatter';
import AssetThumbnail from '../common/AssetThumbnail';
import VariantBadge from '../common/VariantBadge';

interface AssetCardProps {
  asset: Asset;
  onClick?: () => void;
  showActions?: boolean;
  onAssetSelect?: (asset: Asset) => Promise<void>;
  onCreateVariant?: (asset: Asset) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  onClick,
  showActions = true,
  onAssetSelect,
  onCreateVariant,
}) => {
  const navigate = useNavigate();

  // Helper function to detect variant assets
  const isVariantAsset = (asset: Asset): boolean => {
    // Check assetType field (most reliable)
    if (asset.assetType === 'variant') return true;

    // Check if baseAssetId exists at top level
    if ((asset as any).baseAssetId) return true;

    // Check layer-specific metadata for baseAssetId
    if (
      (asset as any).starMetadata?.baseStarId ||
      (asset as any).looksMetadata?.baseLookId ||
      (asset as any).movesMetadata?.baseMovesId ||
      (asset as any).worldsMetadata?.baseWorldId ||
      (asset as any).songMetadata?.baseSongId
    ) {
      return true;
    }

    // Fallback: Check HFN pattern for variant suffix (e.g., -V01, -V02)
    const hfn = asset.friendlyName || (asset as any).humanFriendlyName || '';
    return /-V\d+/.test(hfn);
  };

  const isVariant = isVariantAsset(asset);

  // Handle double-click navigation to asset details
  const handleDoubleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const assetId = asset._id || asset.id;
    if (assetId) {
      navigate(`/assets/${assetId}`);
    } else {
      console.error(
        'Asset ID is undefined, cannot navigate to details page',
        asset
      );
    }
  };

  // Enhanced click handler
  const handleClick = async (event: React.MouseEvent) => {
    if (onAssetSelect) {
      await onAssetSelect(asset);
    } else if (onClick) {
      onClick();
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const nna = asset.friendlyName || (asset as any).friendlyName || '';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        bgcolor: isVariant ? 'rgba(0, 0, 0, 0.03)' : 'background.paper',
        boxShadow: 1,
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <CardActionArea
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        sx={{ flexGrow: 1 }}
      >
        {/* Media section with smart thumbnail handling */}
        <Box
          sx={{
            height: 200,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px 8px 0 0',
          }}
        >
          {/* Use AssetThumbnail component for smart video/image handling */}
          <AssetThumbnail asset={asset} width={200} height={200} />
        </Box>

        {/* Content */}
        <CardContent sx={{ flexGrow: 1, p: 2, py: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              mb: 1.25,
            }}
          >
            {nna && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  mt: 0.5,
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  color: 'text.secondary',
                }}
              >
                {nna && (
                  <Tooltip title={nna} arrow>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline',
                        maxWidth: '100%',
                      }}
                    >
                      {nna}
                    </span>
                  </Tooltip>
                )}
              </Typography>
            )}
            {showActions && (
              <Box
                sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <Tooltip title="View details" arrow>
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/assets/${asset._id || asset.id}`}
                    onClick={e => {
                      if (!asset._id && !asset.id) {
                        e.preventDefault();
                        console.error(
                          'Asset ID is undefined, cannot navigate to details page',
                          asset
                        );
                      }
                    }}
                    color="default"
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {onCreateVariant && (
                  <Tooltip title="Create variant" arrow>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCreateVariant(asset);
                      }}
                      color="default"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          <Typography
            variant="h6"
            component="div"
            sx={{
              mb: 0.5,
              flex: 1,
              minWidth: 0,
              fontSize: '1.05rem',
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              color: 'text.primary',
            }}
          >
            {formatCompositeHfn(asset.name)}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              height: '3em',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.45,
            }}
          >
            {asset.description || 'No description provided'}
          </Typography>

          {/* Metadata block: date, layer, NNA, created-by (no divider) */}
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Created {formatDate(asset.createdAt)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Chip
                  label={asset.layer}
                  size="small"
                  color="secondary"
                  sx={{
                    fontSize: '0.7rem',
                    height: '18px',
                    fontWeight: 'bold',
                    borderRadius: 1,
                  }}
                />
                {isVariant && <VariantBadge />}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AssetCard;
